import React, { useState } from "react";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Paper,
  Alert,
  CircularProgress,
  Grid,
} from "@mui/material";
import {
  Email as EmailIcon,
  LockReset as LockResetIcon,
  Verified as VerifiedIcon,
} from "@mui/icons-material";

const PasswordResetPage = () => {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [step, setStep] = useState(1); // 1: email, 2: OTP, 3: new password
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Mock function to check if email is registered
  const isEmailRegistered = async (email) => {
    // In a real app, this would be an API call
    const registeredEmails = ["kiranjoshy123@gmail.com", "test@test.com"];
    return registeredEmails.includes(email);
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const isRegistered = await isEmailRegistered(email);
      if (!isRegistered) {
        throw new Error("This email is not registered with us");
      }

      // Simulate OTP sending
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setOtpSent(true);
      setStep(2);
      startCountdown();
      setSuccess("OTP sent to your email address");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const startCountdown = () => {
    setCountdown(60);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // In a real app, verify OTP with backend
      if (otp !== "123456") {
        // Mock OTP for demo
        throw new Error("Invalid OTP");
      }

      setStep(3);
      setSuccess("OTP verified successfully");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // In a real app, call API to reset password
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setSuccess(
        "Password reset successfully! You can now login with your new password."
      );
      // Redirect to login after delay
      setTimeout(() => (window.location.href = "/login"), 2000);
    } catch (err) {
      setError(err.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    setError("");

    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      startCountdown();
      setSuccess("New OTP sent to your email address");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {step === 1 && (
              <EmailIcon color="primary" sx={{ fontSize: 40, mb: 2 }} />
            )}
            {step === 2 && (
              <VerifiedIcon color="primary" sx={{ fontSize: 40, mb: 2 }} />
            )}
            {step === 3 && (
              <LockResetIcon color="primary" sx={{ fontSize: 40, mb: 2 }} />
            )}

            <Typography component="h1" variant="h5">
              {step === 1 && "Reset Your Password"}
              {step === 2 && "Verify OTP"}
              {step === 3 && "Create New Password"}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1, textAlign: "center" }}
            >
              {step === 1 && "Enter your registered email to receive an OTP"}
              {step === 2 && `We sent a 6-digit code to ${email}`}
              {step === 3 && "Create a strong new password for your account"}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 3, mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mt: 3, mb: 2 }}>
              {success}
            </Alert>
          )}

          {step === 1 && (
            <Box component="form" onSubmit={handleSendOtp} sx={{ mt: 3 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Registered Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{ mt: 3, mb: 2, py: 1.5 }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Send OTP"
                )}
              </Button>
              <Grid container justifyContent="flex-end">
                <Grid item>
                  <Link href="/login" variant="body2">
                    Remember your password? Sign In
                  </Link>
                </Grid>
              </Grid>
            </Box>
          )}

          {step === 2 && (
            <Box component="form" onSubmit={handleVerifyOtp} sx={{ mt: 3 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="otp"
                label="6-digit OTP"
                name="otp"
                autoComplete="off"
                autoFocus
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                inputProps={{ maxLength: 6 }}
              />
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}
              >
                <Typography variant="body2" color="text.secondary">
                  {countdown > 0
                    ? `Resend OTP in ${countdown}s`
                    : "Didn't receive OTP?"}
                </Typography>
                <Button
                  size="small"
                  disabled={countdown > 0 || loading}
                  onClick={handleResendOtp}
                >
                  Resend OTP
                </Button>
              </Box>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || otp.length !== 6}
                sx={{ mt: 3, mb: 2, py: 1.5 }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Verify OTP"
                )}
              </Button>
            </Box>
          )}

          {step === 3 && (
            <Box component="form" onSubmit={handleResetPassword} sx={{ mt: 3 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                name="newPassword"
                label="New Password"
                type="password"
                id="newPassword"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || newPassword.length < 8}
                sx={{ mt: 3, mb: 2, py: 1.5 }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Reset Password"
                )}
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default PasswordResetPage;
